<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="font-weight-bolder mb-0">System Status</h5>
      <b-button
        variant="outline-secondary"
        class="text-body font-weight-bolder"
        :disabled="loading"
        @click="fetchStatus"
      >
        <feather-icon icon="RefreshCcwIcon" /> Refresh Stats
      </b-button>
    </div>

    <b-row class="mt-2">
      <b-col md="4">
        <b-card border-variant="secondary">
          <div class="d-flex align-items-center" style="gap: 12px">
            <div
              class="rounded-pill d-flex align-items-center justify-content-center"
              style="width: 36px; height: 36px; background: #dbeafe"
            >
              <feather-icon icon="HardDriveIcon" style="color: #155dfc" />
            </div>
            <div>
              <span class="text-muted">CPU Usage</span>
              <h4 class="font-weight-bolder mb-0">
                {{ metrics.cpuUsage != null ? `${metrics.cpuUsage}%` : "—" }}
              </h4>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col md="4">
        <b-card border-variant="secondary">
          <div class="d-flex align-items-center" style="gap: 12px">
            <div
              class="rounded-pill d-flex align-items-center justify-content-center"
              style="width: 36px; height: 36px; background: #dcfce7"
            >
              <feather-icon icon="DatabaseIcon" style="color: #00a63e" />
            </div>
            <div>
              <span class="text-muted">Memory Usage</span>
              <h4 class="font-weight-bolder mb-0">
                {{
                  metrics.memoryUsage != null ? `${metrics.memoryUsage}%` : "—"
                }}
              </h4>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col md="4">
        <b-card border-variant="secondary">
          <div class="d-flex align-items-center" style="gap: 12px">
            <div
              class="rounded-pill d-flex align-items-center justify-content-center"
              style="width: 36px; height: 36px; background: #ffedd4"
            >
              <feather-icon icon="MonitorIcon" style="color: #f54a00" />
            </div>
            <div>
              <span class="text-muted">Response Time</span>
              <h4 class="font-weight-bolder mb-0">
                {{
                  metrics.responseTime != null
                    ? `${metrics.responseTime}ms`
                    : "—"
                }}
              </h4>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Service Status -->
    <b-row>
      <b-col cols="12" class="d-flex align-items-center mb-1" style="gap: 16px">
        <h5 class="font-weight-bolder mb-0">Service Status</h5>
        <div
          class="d-flex align-items-center"
          style="gap: 12px; font-size: 11px"
        >
          <span class="d-flex align-items-center" style="gap: 4px">
            <span class="status-dot bg-success"></span> Health Check
          </span>
          <span class="d-flex align-items-center" style="gap: 4px">
            <span class="status-dot bg-primary"></span> Data Feed
          </span>
          <span class="d-flex align-items-center" style="gap: 4px">
            <span class="status-dot bg-warning"></span> Stale Data
          </span>
        </div>
        <small v-if="checkedAt" class="text-muted ml-auto">
          Last checked: {{ formatCheckedAt(checkedAt) }}
        </small>
      </b-col>

      <b-col v-if="loading" cols="12" class="text-center py-3">
        <b-spinner variant="primary" />
      </b-col>

      <template v-else>
        <b-col
          v-for="service in services"
          :key="service.key"
          md="6"
          class="mb-1"
        >
          <b-card border-variant="secondary" body-class="py-75 px-1">
            <div class="d-flex align-items-center justify-content-between">
              <span>{{ service.name }}</span>
              <div class="d-flex align-items-center" style="gap: 6px">
                <span
                  class="status-dot"
                  :class="healthCheckClass(service.healthCheck)"
                  :title="`Health Check: ${service.healthCheck}`"
                ></span>
                <span
                  class="status-dot"
                  :class="dataFeedClass(service.dataFeed)"
                  :title="`Data Feed: ${service.dataFeed}`"
                ></span>
                <span
                  class="status-dot"
                  :class="staleDataClass(service.staleData)"
                  :title="`Stale Data: ${service.staleData}`"
                ></span>
              </div>
            </div>
          </b-card>
        </b-col>
      </template>
    </b-row>
  </b-card>
</template>

<script>
import { BCard, BButton, BRow, BCol, BSpinner } from "bootstrap-vue";
import { mapState, mapActions } from "vuex";

export default {
  components: { BCard, BButton, BRow, BCol, BSpinner },
  computed: {
    ...mapState("settings/system-status", ["loading", "metrics", "services", "checkedAt"]),
  },
  created() {
    this.fetchStatus();
  },
  methods: {
    ...mapActions("settings/system-status", { fetchStatus: "getSystemStatus" }),
    healthCheckClass(value) {
      return {
        "bg-success": value === "healthy",
        "bg-warning": value === "degraded",
        "bg-danger": value === "unhealthy",
        "bg-secondary": value === "unknown" || !value,
      };
    },
    dataFeedClass(value) {
      return {
        "bg-primary": value === "active",
        "bg-danger": value === "inactive",
        "bg-light border": value === "not_applicable",
        "bg-secondary": value === "unknown" || !value,
      };
    },
    staleDataClass(value) {
      return {
        "bg-success": value === "fresh",
        "bg-danger": value === "stale",
        "bg-light border": value === "not_applicable",
        "bg-secondary": value === "unknown" || !value,
      };
    },
    formatCheckedAt(iso) {
      return new Date(iso).toLocaleTimeString();
    },
  },
};
</script>

<style scoped>
.status-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
